import React, { useCallback, useEffect, useMemo } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, HelpCallToAction, Indicator, NavigationHeader, Spacer } from 'design-system';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { useAmplitudeOnMountEvent } from 'features/invest/utils/analytics';
import { selectUserTier } from 'features/premium/selectors/tier';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import createStyleSheets from 'utils/createStyleSheets';
import { extractKeyFromKey } from 'utils/flatListHelpers';
import Links from 'utils/links';
import { OnboardingFlow } from 'utils/types/navigation';
import { getPotsInformation, savePotsOnboardingState } from '../actions';
import PotTypeCard from '../components/PotTypeCard';
import useNavigateToCreatePot from '../hooks/useNavigateToCreatePot';
import useNavigateToNextScreenForPotsOnboarding from '../hooks/useNavigateToNextScreenForPotsOnboarding';
import { selectHasCashIsaPot, selectPotsStatuses } from '../reducers/selectors';
export const SelectPotTypeScreen = ({ route, navigation }) => {
    const dispatch = useAppDispatch();
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const scrollY = useSharedValue(0);
    const colors = useColors();
    const activeTier = useAppSelector(selectUserTier);
    const products = useAppSelector((store) => store.pots.products);
    const userTaxId = useAppSelector((state) => state.user.user.taxId);
    const hasVisibleCashIsa = useAppSelector((store) => store.pots.hasVisibleCashIsa);
    const hasCashIsaPot = useAppSelector(selectHasCashIsaPot);
    const { status } = useAppSelector(selectPotsStatuses);
    const styles = useStyles(styleSet);
    const [, , fetching, fetch] = useFetchOld(muteErrorAndResult);
    useEffect(() => {
        // Fetching latest info every time the user is in this screen
        fetch(getPotsInformation());
    }, [status]);
    useEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useAmplitudeOnMountEvent(OnboardingFlow.Pots, !!mountedOnAnotherScreen, 'Pot Account Type Screen');
    const navigateToNextScreen = useNavigateToNextScreenForPotsOnboarding();
    const navigateToCreatePot = useNavigateToCreatePot();
    const handlePotTypeSelect = useCallback((productId, isProductCashISA) => () => {
        if (route.params?.isPotsOnboarding) {
            dispatch(savePotsOnboardingState({ productId }));
            navigateToNextScreen();
            return;
        }
        if (isProductCashISA && !userTaxId) {
            navigation.navigate('InvestInsurance', { flow: OnboardingFlow.Pots, productId });
            return;
        }
        navigateToCreatePot(productId);
    }, [navigateToCreatePot, navigateToNextScreen, route.params?.isPotsOnboarding, userTaxId]);
    const onHelpPress = useCallback(() => {
        Links.openKnownLink('potsHelp');
    }, []);
    const renderItem = useCallback(({ item }) => {
        if (!item.isVisible || item.type === 'BASIC' || (item.isProductCashISA && !hasCashIsaPot)) {
            return null;
        }
        return (<PotTypeCard name={item.name} productId={item.id} bankLogo={item.bankLogo} description={item.description} fscsBrochure={item.fscsBrochure} isCashISA={item.isProductCashISA} isOnboarding={route.params?.isPotsOnboarding} interestPaymentFrequency={item.interestPaymentFrequency} onPress={handlePotTypeSelect(item.id, item.isProductCashISA)} showInterest={item.type === 'INTEREST' || item.type === 'INTEREST_NOTICE'} isRecommended={hasVisibleCashIsa && hasCashIsaPot ? item.isRecommended && item.isProductCashISA : item.isRecommended}/>);
    }, [handlePotTypeSelect, hasCashIsaPot, hasVisibleCashIsa, route.params?.isPotsOnboarding]);
    const renderFooter = useMemo(() => (<>
        <Spacer.H16 />
        <HelpCallToAction onDark onPress={onHelpPress} title="Any questions about pots?" subTitle="Learn everything about them"/>
        <Spacer h={24}/>
        <View style={styles.footerText}>
          <Text TextThin-14 Secondary centered>
            Your rate will vary based on your Emma plan.
          </Text>
        </View>
      </>), [onHelpPress, styles.footerText]);
    const renderHeader = useMemo(() => {
        if (route.params?.isPotsOnboarding) {
            return <ScreenInfoHeader title="Which pot would you like to open?" style={styles.screenInfoHeader}/>;
        }
        return null;
    }, [route.params?.isPotsOnboarding, styles.screenInfoHeader]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const outputColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const scrollHandler = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    return (<AppView withFrame={false}>
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={outputColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Pots}/> : undefined} title={route.params?.isPotsOnboarding ? '' : 'Create pot'}/>
      {fetching || !activeTier ? (<View style={styles.indicatorContainer}>
          <Indicator dark/>
        </View>) : (<Animated.FlatList data={products} renderItem={renderItem} scrollEventThrottle={16} onScroll={scrollHandler} ListHeaderComponent={renderHeader} ListFooterComponent={renderFooter} keyExtractor={extractKeyFromKey('id')} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>)}
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex1: {
        flex: 1,
    },
    indicatorContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    screenInfoHeader: {
        paddingBottom: rem(8),
    },
    footerText: {
        paddingHorizontal: rem(16),
    },
}));
export default SelectPotTypeScreen;
